/**
 * MCP integration tokens — the credential behind the one-line connection string
 *
 *   https://api.hotline.red/mcp/<token>
 *
 * A token is minted per widget instance in the dashboard, shown exactly once,
 * and stored only as a SHA-256 hash. It authorizes the integration MCP server:
 * reading docs and embed code, diagnosing origins, running end-to-end tests,
 * reporting recent feedback, and additive origin provisioning. It is NOT the
 * instance secret (`sk_*`) and never grants dashboard access.
 *
 * `?lang=` on the connection string is RESERVED for localized tool output; it is
 * accepted today and English is served regardless.
 */
import type { z } from 'zod';
import { iso, object, string } from 'zod';

/** One token as listed in the dashboard. The hash never leaves the server. */
export const McpTokenSchema = object({
  id: string().min(1),
  label: string().max(256),
  createdAt: iso.datetime(),
  lastUsedAt: iso.datetime().nullable(),
  revokedAt: iso.datetime().nullable(),
});
export type McpToken = z.infer<typeof McpTokenSchema>;

/** Returned ONCE at creation — the only time the raw token exists outside a hash. */
export const CreatedMcpTokenSchema = object({
  id: string().min(1),
  /** The raw `hmcp_*` token. The dashboard composes the connection string from it. */
  token: string().min(1),
  label: string().max(256),
  createdAt: iso.datetime(),
});
export type CreatedMcpToken = z.infer<typeof CreatedMcpTokenSchema>;

/** Create-request body. */
export const CreateMcpTokenSchema = object({
  label: string().trim().max(256).default(''),
});
export type CreateMcpToken = z.infer<typeof CreateMcpTokenSchema>;
